import { defer, distinctUntilChanged, filter, map, mergeWith, skip } from 'rxjs'
import { clearMemoized, memoize } from '@/utils/memoize-util'
import { createAuthResolutionWaiter } from '@/features/iam/auth/auth-resolution'
import { env } from '@/infra/env'
import { localStorage, sessionStorage } from '@/infra/storage'
import { Id } from '@/infra/id'
import { pageActivityStream } from '@/infra/page-activity'
import { userActive$ } from '@/utils/browser-util'
import type { Workspace } from '@/features/iam/workspaces/Workspace'
import type { Member } from '@/features/iam/members/Member'
import type { Team } from '@/features/iam/teams/Team'
import type { Provider } from '@/features/iam/providers/Provider'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { GetLocationFn, NavigateFn } from '@/RootStore'
import type {
  IsBillingEnabledFn,
  ProfilePaymentMethodsViewModelFactory,
  StripeConnectCallbackModalViewModelFactory,
  TeamPaymentsViewModelFactory,
} from '@/features/billing'
import type {
  IsPayoutsEnabledFn,
  ProfilePayoutMethodViewModelFactory,
} from '@/features/payouts'
import type {
  FetchNotificationPreferencesFn,
  SaveNotificationPreferencesFn,
} from '@/features/notifications'
import type { OnlineStatusForUserFn } from '@/features/messaging'
import type { GetAuthTokenDelegate } from '@/features/shell/platform/types'
import { IamApi } from '@/features/iam/api/iam-api'
import { AuthStore, createAuthProvider } from '@/features/iam/auth/AuthStore'
import { MultiFactorAuthStore } from '@/features/iam/auth/MultiFactorAuthStore'
import { UserStore } from '@/features/iam/users/UserStore'
import {
  type FetchWorkspaceOptions,
  type ListWorkspacesQuery,
  WorkspaceStore,
} from '@/features/iam/workspaces/WorkspaceStore'
import { CustomerStore } from '@/features/iam/customers/CustomerStore'
import { ClientAssignmentStore } from '@/features/iam/customers/ClientAssignmentStore'
import {
  type CreateMemberManuallyParams,
  type CreateMemberParams,
  type GenerateMagicLinkParams,
  type MagicLink,
  MemberStore,
  type SearchMembersQuery,
  type UpdateMemberParams,
} from '@/features/iam/members/MemberStore'
import {
  type FetchSearchableUserParams,
  SearchableUserStore,
  type SearchUsersQuery,
} from '@/features/iam/users/SearchableUserStore'
import { SessionStore } from '@/features/iam/auth/SessionStore'
import { WorkspaceMembershipStore } from '@/features/iam/workspaces/WorkspaceMembershipStore'
import {
  type ListTeamsQuery,
  type ListUserTeamsQuery,
  TeamStore,
} from '@/features/iam/teams/TeamStore'
import {
  type ListTeamRolesQuery,
  TeamRoleStore,
} from '@/features/iam/teams/TeamRoleStore'
import {
  type AddTeamMemberParams,
  type ListUserTeamMembersParams,
  type ListTeamMembersQuery,
  type ListTeamMembersForWorkspaceMemberParams,
  TeamMemberStore,
} from '@/features/iam/teams/TeamMemberStore'
import { InvitationStore } from '@/features/iam/invitations/InvitationStore'
import { type ListRolesQuery, RoleStore } from '@/features/iam/roles/RoleStore'
import {
  type ListSkillsQuery,
  SkillStore,
} from '@/features/iam/skills/SkillStore'
import { TermsOfUseStore } from '@/features/iam/terms/TermsOfUseStore'
import {
  type UpdateUserDataParams,
  UserDataStore,
} from '@/features/iam/users/UserDataStore'
import { ProviderStore } from '@/features/iam/providers/ProviderStore'
import { User } from '@/features/iam/users/User'
import { lastSeenSync } from '@/features/iam/members/last-seen-sync'
import { workspacePathRestore } from '@/features/iam/workspaces/workspace-path-restore'
import { createServerRenderedWorkspaceReader } from '@/features/iam/workspaces/server-rendered-workspace'
import { AuthenticationViewModel } from '@/features/iam/auth/Authentication/AuthenticationViewModel'
import { CompleteRegistrationViewModel } from '@/features/iam/auth/CompleteRegistration/CompleteRegistrationViewModel'
import { TermsModalViewModel } from '@/features/iam/terms/TermsModal/TermsModalViewModel'
import { ProSoftwareReviewModalViewModel } from '@/features/iam/providers/ProSoftwareReviewModal/ProSoftwareReviewModalViewModel'
import type { ProDetailsViewModel } from '@/features/iam/providers/ProDetails/ProDetailsViewModel'
import { toStream } from '@/utils/mobx-util'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import type { CursorPaged } from '@/infra/paging'
import type { Membership } from '@/features/iam/workspaces/Membership'
import type { TeamMember } from '@/features/iam/teams/TeamMember'
import type { Role } from '@/features/iam/roles/Role'
import type { Skill } from '@/features/iam/skills/Skill'
import type { TeamRole } from '@/features/iam/teams/TeamRole'
import type { UserData } from '@/features/iam/users/UserData'
import type { SearchableUser } from '@/features/iam/users/SearchableUser'
import type {
  CreateCustomerParams,
  CreatedCustomer,
} from '@/features/iam/customers/CustomerStore'
import type { ClientAssignment } from '@/features/iam/customers/ClientAssignmentStore'
import {
  bulkCreateCustomers as bulkCreateCustomersInBatches,
  type BulkSubmissionResult,
} from '@/features/iam/customers/bulk-create-util'
import type { ParsedCustomerRow } from '@/features/iam/customers/customer-csv'

/**
 * Props used to prime a user in the store with data to use before the user
 * has been fully loaded.
 */
export interface PrimeUserProps {
  publicId: Id
  id?: Id
  givenName?: string
  familyName?: string
  email?: string
  picture?: string
}

/**
 * Delegate used for priming a user.
 */
export type PrimeUserFn = (props: PrimeUserProps) => User

/**
 * Props used to prime a workspace in the store.
 */
export interface PrimeWorkspaceProps {
  id: Id
}

/**
 * Delegate used for priming a workspace. Returns `undefined` when there is no ID.
 */
export type PrimeWorkspaceFn = (
  props: PrimeWorkspaceProps,
) => Workspace | undefined

/**
 * Reads the workspace of the custom domain the app is served from, if any.
 */
export type ReadServerRenderedWorkspaceFn = () => Workspace | null

/**
 * Member data that is shown before the member has been fully loaded.
 */
export interface PrimeMemberDetails {
  givenName?: string
  familyName?: string
  displayName?: string
  email?: string
  picture?: string
}

/**
 * Props used to prime a member by workspace and (internal) user ID.
 */
export interface PrimeMemberProps extends PrimeMemberDetails {
  workspaceId: Id
  userId: Id
  userPublicId?: Id
}

/**
 * Delegate used for priming a member by workspace and (internal) user ID.
 */
export type PrimeMemberFn = (props: PrimeMemberProps) => Member

/**
 * Props used to prime a member by workspace and user public ID.
 */
export interface PrimeMemberByPublicIdProps extends PrimeMemberDetails {
  workspaceId: Id
  userPublicId: Id
  userId?: Id
}

/**
 * Delegate used for priming a member by workspace and user public ID.
 */
export type PrimeMemberByPublicIdFn = (
  props: PrimeMemberByPublicIdProps,
) => Member

/**
 * Props used to prime a team in the store.
 */
export interface PrimeTeamProps {
  id: Id
}

/**
 * Delegate used for priming a team.
 */
export type PrimeTeamFn = (props: PrimeTeamProps) => Team

/**
 * Props used to prime a provider in the store.
 */
export interface PrimeProviderProps {
  userPublicId: Id
}

/**
 * Delegate used for priming a provider. Returns `undefined` when there is no ID.
 */
export type PrimeProviderFn = (
  props: PrimeProviderProps,
) => Provider | undefined

/**
 * Getter for the Current User.
 */
export type GetCurrentUserFn = () => User

/**
 * Getter for the Current Workspace.
 */
export type GetCurrentWorkspaceFn = () => Workspace

/**
 * Getter for the Current Workspace, or `null` when none is selected.
 */
export type FindCurrentWorkspaceFn = () => Workspace | null

/**
 * Getter for the Current Workspace Member.
 */
export type GetCurrentMemberFn = (workspaceId?: Id) => Member

/**
 * Waits for authentication to resolve.
 */
export type WaitForAuthResolutionFn = (
  signal?: AbortSignal,
) => Promise<AuthResolution>

/**
 * Authentication resolution from {@link WaitForAuthResolutionFn}.
 */
export interface AuthResolution {
  /**
   * Whether the user is authenticated.
   */
  authenticated: boolean

  /**
   * Which workspace has been selected, if any.
   */
  workspace: Workspace | null
}

/**
 * Logs the current user out.
 */
export type LogoutFn = () => Promise<void>

/**
 * Gets a loaded workspace.
 */
export type GetWorkspaceFn = (id: Id) => Workspace | undefined

/**
 * Fetches a workspace.
 */
export type FetchWorkspaceFn = (
  id: Id,
  options?: FetchWorkspaceOptions,
  signal?: AbortSignal,
) => Promise<Workspace | null>

/**
 * Lists workspaces.
 */
export type ListWorkspacesFn = (
  query: ListWorkspacesQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<Workspace>>

/**
 * The workspace picked in the workspace selector.
 */
export interface WorkspaceSelection {
  workspace: Workspace | null

  /**
   * Whether lists should span all of the user's workspaces by default.
   */
  allWorkspaces: boolean
}

/**
 * Gets the workspace picked in the workspace selector.
 */
export type GetWorkspaceSelectionFn = () => WorkspaceSelection

/**
 * Gets the current user's loaded workspace memberships.
 */
export type GetCurrentUserWorkspaceMembershipsFn = () => Membership[]

/**
 * Fetches the current user's workspace memberships.
 */
export type ListWorkspaceMembershipsFn = (
  options?: ListWorkspaceMembershipsOptions,
  signal?: AbortSignal,
) => Promise<Membership[]>

/**
 * Options for {@link ListWorkspaceMembershipsFn}.
 */
export interface ListWorkspaceMembershipsOptions {
  /**
   * Fetches again instead of reusing the memberships fetched before.
   */
  skipCache?: boolean
}

/**
 * Fetches a user's workspace memberships.
 */
export type ListUserWorkspaceMembershipsFn = (
  userPublicId: Id,
  inactive?: boolean,
  signal?: AbortSignal,
) => Promise<Membership[]>

/**
 * Gets the loaded members of a workspace.
 */
export type GetWorkspaceMembersFn = (workspaceId: Id) => Member[]

/**
 * Props for {@link FetchMemberFn}.
 */
export interface FetchMemberProps {
  workspaceId: Id
  userPublicId: Id
}

/**
 * Gets a loaded member of a workspace.
 */
export type GetMemberFn = (workspaceId: Id, userId: Id) => Member | undefined

/**
 * Fetches a member by workspace and user public ID.
 */
export type FetchMemberFn = (
  props: FetchMemberProps,
  signal?: AbortSignal,
) => Promise<Member | null>

/**
 * Fetches members by workspace and user public ID.
 */
export type FetchMembersByPublicIdsFn = (
  ids: readonly FetchMemberProps[],
  signal?: AbortSignal,
) => Promise<(Member | null)[]>

/**
 * Searches for members.
 */
export type SearchMembersFn = (
  query: SearchMembersQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<Member>>

/**
 * Creates a member.
 */
export type CreateMemberFn = (params: CreateMemberParams) => Promise<Member>

/**
 * Creates another user's member on their behalf.
 */
export type CreateMemberManuallyFn = (
  params: CreateMemberManuallyParams,
) => Promise<Member>

/**
 * Updates a member.
 */
export type UpdateMemberFn = (params: UpdateMemberParams) => Promise<Member>

/**
 * Adds a role to a member.
 */
export type AddMemberRoleFn = (
  workspaceId: Id,
  userPublicId: Id,
  roleId: Id,
) => Promise<Member>

/**
 * Adds or removes skills of a member.
 */
export type UpdateMemberSkillsFn = (
  workspace: Workspace,
  userPublicId: Id,
  skills: Skill[],
) => Promise<Member>

/**
 * Starts impersonating a member.
 */
export type ImpersonateMemberFn = (
  userPublicId: Id,
  workspaceId: Id,
  callback: string,
) => Promise<{ url: string }>

/**
 * Generates a magic link that logs in as a member.
 */
export type GenerateMemberMagicLinkFn = (
  params: GenerateMagicLinkParams,
) => Promise<MagicLink>

/**
 * Makes the current platform admin a member, or the owner, of a workspace.
 */
export type PlatformAdminWorkspaceFn = (workspaceId: Id) => Promise<void>

/**
 * Gets a loaded team.
 */
export type GetTeamFn = (id: Id) => Team | undefined

/**
 * Gets the loaded teams of a workspace.
 */
export type GetWorkspaceTeamsFn = (workspaceId: Id) => Team[]

/**
 * Lists the teams of a workspace.
 */
export type ListTeamsFn = (
  query: ListTeamsQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<Team>>

/**
 * Lists a user's teams.
 */
export type ListUserTeamsFn = (
  query: ListUserTeamsQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<Team>>

/**
 * Gets a loaded team member.
 */
export type GetTeamMemberFn = (
  teamId: Id,
  userPublicId: Id,
) => TeamMember | undefined

/**
 * Gets the loaded members of a team.
 */
export type GetTeamMembersFn = (teamId: Id) => TeamMember[]

/**
 * Gets a user's loaded team members in a team.
 */
export type FilterTeamMembersFn = (publicId: Id, teamId: Id) => TeamMember[]

/**
 * Gets the loaded team members of a workspace member. Defaults to the current
 * user.
 */
export type GetTeamMembersForWorkspaceMemberFn = (
  workspaceId?: Id,
  userId?: Id,
) => TeamMember[]

/**
 * Fetches a team member.
 */
export type FetchTeamMemberFn = (
  teamId: Id,
  userPublicId: Id,
  signal?: AbortSignal,
) => Promise<TeamMember | null>

/**
 * Fetches a user's team members across workspaces. Defaults to the current
 * user.
 */
export type ListUserTeamMembersFn = (
  params?: ListUserTeamMembersParams,
  signal?: AbortSignal,
) => Promise<TeamMember[]>

/**
 * Fetches a user's team members in a workspace. Defaults to the current
 * member.
 */
export type ListUserTeamMembersForWorkspaceFn = (
  params?: ListUserTeamMembersParams,
  signal?: AbortSignal,
) => Promise<TeamMember[]>

/**
 * Lists the members of a team.
 */
export type ListTeamMembersFn = (
  query: ListTeamMembersQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<TeamMember>>

/**
 * Lists team members by user public ID.
 */
export type ListTeamMembersForWorkspaceMemberFn = (
  params: ListTeamMembersForWorkspaceMemberParams,
  signal?: AbortSignal,
) => Promise<TeamMember[]>

/**
 * Adds a member to a team by user public ID.
 */
export type AddTeamMemberFn = (
  params: AddTeamMemberParams,
) => Promise<TeamMember>

/**
 * Fetches the roles of a workspace.
 */
export type ListRolesFn = (
  query: ListRolesQuery,
  signal?: AbortSignal,
) => Promise<Role[]>

/**
 * Gets the loaded roles of a workspace.
 */
export type GetWorkspaceRolesFn = (workspaceId: Id) => Role[]

/**
 * Lists skills.
 */
export type ListSkillsFn = (
  query: ListSkillsQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<Skill>>

/**
 * Gets the loaded skills of a workspace.
 */
export type GetWorkspaceSkillsFn = (workspaceId: Id) => Skill[]

/**
 * Lists team roles.
 */
export type ListTeamRolesFn = (
  query: ListTeamRolesQuery,
  signal?: AbortSignal,
) => Promise<TeamRole[]>

/**
 * Gets the loaded team roles of a workspace.
 */
export type GetWorkspaceTeamRolesFn = (workspaceId: Id) => TeamRole[]

/**
 * Gets loaded user data. Returns `null` when not loaded.
 */
export type GetUserDataFn = (publicId: Id) => UserData | null

/**
 * Fetches user data.
 */
export type FetchUserDataFn = (
  publicId: Id,
  options?: FetchUserDataOptions,
  signal?: AbortSignal,
) => Promise<UserData>

/**
 * Options for {@link FetchUserDataFn}.
 */
export interface FetchUserDataOptions {
  /**
   * Fetches again instead of reusing the user data fetched before.
   */
  skipCache?: boolean
}

/**
 * Updates user data, then refetches it in the background.
 */
export type UpdateUserDataFn = (
  publicId: Id,
  params: UpdateUserDataParams,
) => Promise<void>

/**
 * Searches users across workspaces.
 */
export type SearchUsersFn = (
  query: SearchUsersQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<SearchableUser>>

/**
 * Fetches a searchable user.
 */
export type FetchSearchableUserFn = (
  publicId: Id,
  params: FetchSearchableUserParams,
  signal?: AbortSignal,
) => Promise<SearchableUser>

/**
 * Gets the loaded searchable users.
 */
export type GetSearchableUsersFn = () => SearchableUser[]

/**
 * Creates a customer in a workspace.
 */
export type CreateCustomerFn = (
  params: CreateCustomerParams,
) => Promise<CreatedCustomer>

/**
 * Fetches a client's assignment, or `null` when the current member may not see
 * it.
 */
export type FetchClientAssignmentFn = (
  workspaceId: Id,
  clientUserPublicId: Id,
  signal?: AbortSignal,
) => Promise<ClientAssignment | null>

/**
 * Params for {@link BulkCreateCustomersFn}.
 */
export interface BulkCreateCustomersParams {
  workspaceId: Id
  customers: ParsedCustomerRow[]
  advisorUserPublicId?: Id
}

/**
 * Creates customers in rate-limited batches, reporting each customer's
 * outcome.
 */
export type BulkCreateCustomersFn = (
  params: BulkCreateCustomersParams,
) => Promise<BulkSubmissionResult[]>

/**
 * Creates the view model for a pro's details.
 */
export type ProDetailsViewModelFactory = (
  userPublicId: Id,
  editable: boolean,
) => ProDetailsViewModel

/**
 * Prompts for the terms of use until they are accepted. `null` checks the
 * platform terms.
 */
export type EnsureTermsAcceptedFn = (workspaceId: Id | null) => Promise<void>

/**
 * Reactively determines whether the current user belongs to more than one
 * workspace.
 */
export type HasMultipleWorkspacesFn = () => boolean

/**
 * Reactively determines whether the workspace selector is open.
 */
export type IsWorkspaceSelectorOpenFn = () => boolean

/**
 * Opens or closes the workspace selector.
 */
export type ToggleWorkspaceSelectorFn = () => void

/**
 * Opens the search for a workspace to join.
 */
export type OpenWorkspaceSearchFn = () => void

/**
 * Reactively determines whether the current user has enrolled in multi-factor
 * authentication.
 */
export type IsEnrolledInMultiFactorAuthFn = () => boolean

/**
 * Gets the URL where the current user enrolls in multi-factor authentication.
 */
export type GetMultiFactorAuthEnrollmentUrlFn = () => Promise<string>

/**
 * Gets the enterprise connection the current user signed in with, if any.
 */
export type GetEnterpriseConnectionFn = () => string | null

/**
 * Options for {@link IamFeature}.
 */
export interface IamFeatureOptions {
  /**
   * Navigates within the app.
   */
  navigate: NavigateFn

  /**
   * Gets the current location.
   */
  getLocation: GetLocationFn

  /**
   * Delegate for getting the auth token delegate.
   */
  getAuthTokenDelegate: GetAuthTokenDelegate

  /**
   * Factory for creating a new alert.
   */
  newAlert: AlertFactory

  /**
   * Creates a handler that alerts a failed write and rethrows.
   */
  createAlertErrorHandler: AlertErrorHandlerFactory

  /**
   * Creates a wrapper that alerts a failed read with the option to retry.
   */
  createAlertRetryWrapper: AlertRetryWrapperFactory

  /**
   * Streams whether a user is online.
   */
  onlineStatusForUser: OnlineStatusForUserFn

  /**
   * Whether billing is enabled for the current workspace.
   */
  isBillingEnabled: IsBillingEnabledFn

  /**
   * Factory for the profile's payment methods view model.
   */
  createProfilePaymentMethodsViewModel: PromiseLike<ProfilePaymentMethodsViewModelFactory>

  /**
   * Factory for a team's payments view model.
   */
  createTeamPaymentsViewModel: PromiseLike<TeamPaymentsViewModelFactory>

  /**
   * Factory for the Stripe Connect callback modal view model.
   */
  createStripeConnectCallbackModalViewModel: PromiseLike<StripeConnectCallbackModalViewModelFactory>

  /**
   * Whether payouts are enabled for the current workspace.
   */
  isPayoutsEnabled: IsPayoutsEnabledFn

  /**
   * Factory for the profile's payout method view model.
   */
  createProfilePayoutMethodViewModel: PromiseLike<ProfilePayoutMethodViewModelFactory>

  /**
   * Fetches a member's notification preferences.
   */
  fetchNotificationPreferences: FetchNotificationPreferencesFn

  /**
   * Saves a member's notification preferences.
   */
  saveNotificationPreferences: SaveNotificationPreferencesFn
}

/**
 * Identity and access: authentication, session, workspaces, members, teams
 * and users.
 */
export type IamFeature = ReturnType<typeof IamFeature>

export function IamFeature(options: IamFeatureOptions) {
  const authStore: AuthStore = new AuthStore({
    provider: createAuthProvider({
      getAuthTokenDelegate: options.getAuthTokenDelegate,
    }),
    navigate: options.navigate,
    getLocation: options.getLocation,
    becomeActive$: pageActivityStream().pipe(
      map((a) => a.focused && a.visible),
      distinctUntilChanged(),
      mergeWith(
        userActive$.pipe(
          // Skip the first emission (it emits current value on subscribe,
          // but we only care about the next change)
          skip(1),
        ),
      ),
      filter(Boolean),
    ),
    userLogout: () => iamApi.users.logout(),
    getLogoutUrl: () => sessionStore.currentWorkspace.config.signup.logoutUrl,
    isFederatedLogout: () => sessionStore.isFederatedLogout,
    authenticate: (session, signal) =>
      sessionStore.authenticate(session, signal),
  })

  const token$ = authStore.token$
  const getCurrentWorkspace: GetCurrentWorkspaceFn = () =>
    sessionStore.currentWorkspace
  const findCurrentWorkspace: FindCurrentWorkspaceFn = () =>
    sessionStore.hasWorkspaceSelected ? sessionStore.currentWorkspace : null
  const getCurrentUser: GetCurrentUserFn = () => sessionStore.currentUser
  const getCurrentMember: GetCurrentMemberFn = (workspaceId?: Id) =>
    sessionStore.getCurrentMember(workspaceId)

  const iamApi = new IamApi({
    baseURL: env.IAM_API,
    token$,
  })

  const userStore: UserStore = new UserStore(
    iamApi,
    (user) => Id.some(user.id) && user.id === sessionStore.currentUser.id,
    options.onlineStatusForUser,
  )

  const primeUser: PrimeUserFn = (props) =>
    userStore.primeUser(props) ?? User.none

  const workspaceStore = new WorkspaceStore(iamApi)
  const readServerRenderedWorkspace =
    createServerRenderedWorkspaceReader(workspaceStore)

  const customerStore = new CustomerStore(iamApi.customers)
  const clientAssignmentStore = new ClientAssignmentStore(
    iamApi.clientAssignments,
  )

  const memberStore = new MemberStore(
    iamApi,
    workspaceStore.primeWorkspace.bind(workspaceStore),
    primeUser,
    (props) => roleStore.roles.set(props),
    (props) => skillStore.skills.set(props),
  )

  const searchableUserStore = new SearchableUserStore(
    iamApi,
    (props) => memberStore.primeFullMember(props),
    (props) => workspaceStore.primeFullWorkspace(props),
    options.onlineStatusForUser,
  )

  const workspaceMembershipStore = new WorkspaceMembershipStore(
    workspaceStore,
    memberStore,
    iamApi,
    getCurrentUser,
  )

  const invitationStore = new InvitationStore(
    iamApi,
    memberStore.setMember.bind(memberStore),
  )
  const userDataStore = new UserDataStore(
    defer(() => sessionStore.currentMember$),
    iamApi,
  )

  const sessionStore: SessionStore = new SessionStore(
    iamApi,
    userStore,
    workspaceStore,
    memberStore,
    workspaceMembershipStore,
    invitationStore,
    userDataStore,
  )

  const multiFactorAuthStore = new MultiFactorAuthStore(iamApi, getCurrentUser)

  const teamStore: TeamStore = new TeamStore(
    iamApi,
    workspaceStore.primeWorkspace.bind(workspaceStore),
    (props) => teamMemberStore.primeTeamMember(props),
  )

  const teamRoleStore = new TeamRoleStore(iamApi)

  const teamMemberStore: TeamMemberStore = new TeamMemberStore(
    iamApi,
    getCurrentMember,
    memberStore.getMember.bind(memberStore),
    memberStore.primeMember.bind(memberStore),
    teamStore.primeFullTeam.bind(teamStore),
    (props) => teamRoleStore.teamRoles.set(props),
    sessionStore.currentMember$,
  )

  const roleStore = new RoleStore(iamApi)
  const skillStore = new SkillStore(iamApi)
  const termsOfUseStore = new TermsOfUseStore(iamApi)
  const iamProviderStore = new ProviderStore(iamApi.providers)

  const acquireCompleteRegistrationViewModel = memoize(
    (passwordRequestId: string | undefined) =>
      new CompleteRegistrationViewModel(
        passwordRequestId,
        userStore,
        options.navigate,
      ),
  )

  const termsModalViewModel = new TermsModalViewModel(
    termsOfUseStore,
    options.createAlertErrorHandler,
  )

  const authenticationViewModel = new AuthenticationViewModel(
    sessionStore,
    workspaceMembershipStore,
    workspaceStore,
    memberStore,
    authStore,
    options.navigate,
    options.newAlert,
    options.createAlertErrorHandler,
    localStorage,
    sessionStorage,
  )

  // Revalidating the authenticated route must not re-run the activation,
  // which would refetch the session and flash the loading screen.
  const activateSession = memoize(
    async (inviteToken: string) => {
      // The authentication screen renders the activation's error.
      await authenticationViewModel
        .fetch(inviteToken || undefined)
        .catch(() => undefined)
      sessionStore.completeWorkspacePreselection()
    },
    { promise: true },
  )
  const activate = (inviteToken?: string) => {
    const activation = activateSession(inviteToken ?? '')
    // Each visit to an invitation link must handle it again, so only the
    // activation in flight is shared.
    if (inviteToken) {
      void activation.then(() => clearMemoized(activateSession, inviteToken))
    }
    return activation
  }

  const waitForAuthResolution: WaitForAuthResolutionFn =
    createAuthResolutionWaiter({
      currentWorkspace$: sessionStore.currentWorkspace$,
      isAuthenticated$: authStore.checkAuthenticationDecision$,
    })

  const logout: LogoutFn = () => authStore.logout()

  const primeWorkspace: PrimeWorkspaceFn = (props) =>
    workspaceStore.primeWorkspace(props)
  const getWorkspace: GetWorkspaceFn = (id) => workspaceStore.workspaces.get(id)
  const fetchWorkspace: FetchWorkspaceFn = (id, options, signal) =>
    workspaceStore.fetchWorkspace(id, options, signal)
  const listWorkspaces: ListWorkspacesFn = (query, signal) =>
    workspaceStore.listWorkspaces(query, signal)

  const getWorkspaceSelection: GetWorkspaceSelectionFn = () => ({
    workspace: authenticationViewModel.workspaceSelectorVm.workspace,
    allWorkspaces:
      authenticationViewModel.workspaceSelectorVm.isDefaultToAllWorkspaces,
  })
  const workspaceSelection$ = toStream(getWorkspaceSelection, true)

  const getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn =
    () => workspaceMembershipStore.getCurrentUserWorkspaceMemberships()
  const listWorkspaceMemberships: ListWorkspaceMembershipsFn = (
    options,
    signal,
  ) => workspaceMembershipStore.listWorkspaceMemberships(options, signal)
  const listUserWorkspaceMemberships: ListUserWorkspaceMembershipsFn = (
    userPublicId,
    inactive,
    signal,
  ) =>
    workspaceMembershipStore.listUserWorkspaceMemberships(
      { userPublicId, inactive },
      signal,
    )

  const primeMember: PrimeMemberFn = (props) => memberStore.primeMember(props)
  const primeMemberByPublicId: PrimeMemberByPublicIdFn = (props) =>
    memberStore.primeMemberByPublicId(props)
  const getWorkspaceMembers: GetWorkspaceMembersFn = (workspaceId) =>
    memberStore.forWorkspace(workspaceId)
  const fetchMember: FetchMemberFn = ({ workspaceId, userPublicId }, signal) =>
    memberStore.fetchMemberByUserPublicId({ workspaceId, userPublicId }, signal)
  const fetchMembersByPublicIds: FetchMembersByPublicIdsFn = (params, signal) =>
    memberStore.fetchManyByPublicIds(params, signal)
  const searchMembers: SearchMembersFn = (query, signal) =>
    memberStore.listMembers(query, signal)
  const searchMembersByPermission: SearchMembersFn = (query, signal) =>
    memberStore.listMembersByPermission(query, signal)
  const createMember: CreateMemberFn = (params) =>
    memberStore.createMember(params)
  const createMemberManually: CreateMemberManuallyFn = (params) =>
    memberStore.createMemberManually(params)
  const updateMember: UpdateMemberFn = (params) =>
    memberStore.updateMember(params)
  const addMemberRole: AddMemberRoleFn = (workspaceId, userPublicId, roleId) =>
    memberStore.addRole(workspaceId, userPublicId, roleId)
  const addMemberSkills: UpdateMemberSkillsFn = (
    workspace,
    userPublicId,
    skills,
  ) => memberStore.addManySkillsByPublicId(workspace, userPublicId, skills)
  const removeMemberSkills: UpdateMemberSkillsFn = (
    workspace,
    userPublicId,
    skills,
  ) => memberStore.removeManySkillsByPublicId(workspace, userPublicId, skills)
  const impersonateMember: ImpersonateMemberFn = (
    userPublicId,
    workspaceId,
    callback,
  ) => memberStore.impersonate(userPublicId, workspaceId, callback)
  const generateMemberMagicLink: GenerateMemberMagicLinkFn = (params) =>
    memberStore.generateMagicLink(params)

  const joinWorkspaceAsAdmin: PlatformAdminWorkspaceFn = (workspaceId) =>
    workspaceStore.joinAsAdmin(workspaceId)
  const promoteToOwner: PlatformAdminWorkspaceFn = (workspaceId) =>
    workspaceStore.promoteToOwner(workspaceId)

  const primeTeam: PrimeTeamFn = (props) => teamStore.primeTeam(props)
  const getTeam: GetTeamFn = (id) => teamStore.teams.get(id)
  const getWorkspaceTeams: GetWorkspaceTeamsFn = (workspaceId) =>
    teamStore.forWorkspace(workspaceId)
  const listTeams: ListTeamsFn = (query, signal) =>
    teamStore.listTeams(query, signal)
  const listUserTeams: ListUserTeamsFn = (query, signal) =>
    teamStore.listUserTeams(query, signal)

  const getTeamMember: GetTeamMemberFn = (teamId, userPublicId) =>
    teamMemberStore.getTeamMember(teamId, userPublicId)
  const getTeamMembers: GetTeamMembersFn = (teamId) =>
    teamMemberStore.getTeamMembers(teamId)
  const filterTeamMembers: FilterTeamMembersFn = (publicId, teamId) =>
    teamMemberStore.filterTeamMembers(publicId, teamId)
  const getTeamMembersForWorkspaceMember: GetTeamMembersForWorkspaceMemberFn = (
    workspaceId,
    userId,
  ) => teamMemberStore.teamMembersForWorkspaceMember(workspaceId, userId)
  const fetchTeamMember: FetchTeamMemberFn = (teamId, userPublicId, signal) =>
    teamMemberStore.fetchTeamMember(teamId, userPublicId, signal)
  const listUserTeamMembers: ListUserTeamMembersFn = (params, signal) =>
    teamMemberStore.listUserTeamMembers(params, signal)
  const listUserTeamMembersForWorkspace: ListUserTeamMembersForWorkspaceFn = (
    params,
    signal,
  ) => teamMemberStore.listUserTeamMembersForWorkspace(params, signal)
  const listTeamMembers: ListTeamMembersFn = (query, signal) =>
    teamMemberStore.listTeamMembers(query, signal)
  const listTeamMembersForWorkspaceMember: ListTeamMembersForWorkspaceMemberFn =
    (params, signal) =>
      teamMemberStore.listTeamMembersForWorkspaceMember(params, signal)
  const addTeamMember: AddTeamMemberFn = (params) =>
    teamMemberStore.addTeamMemberByPublicId(params)

  const listRoles: ListRolesFn = (query, signal) =>
    roleStore.fetchRoles(query, signal)
  const getWorkspaceRoles: GetWorkspaceRolesFn = (workspaceId) =>
    roleStore.forWorkspace(workspaceId)
  const listSkills: ListSkillsFn = (query, signal) =>
    skillStore.listSkills(query, signal)
  const getWorkspaceSkills: GetWorkspaceSkillsFn = (workspaceId) =>
    skillStore.forWorkspace(workspaceId)
  const listTeamRoles: ListTeamRolesFn = (query, signal) =>
    teamRoleStore.listTeamRoles(query, signal)
  const getWorkspaceTeamRoles: GetWorkspaceTeamRolesFn = (workspaceId) =>
    teamRoleStore.forWorkspace(workspaceId)

  const getUserData: GetUserDataFn = (publicId) =>
    userDataStore.getByPublicId(publicId)
  const fetchUserData: FetchUserDataFn = (publicId, options, signal) =>
    userDataStore.fetchByPublicId(publicId, options, signal)
  const updateUserData: UpdateUserDataFn = (publicId, params) =>
    userDataStore.updateUserData(publicId, params)

  const searchUsers: SearchUsersFn = (query, signal) =>
    searchableUserStore.listSearchableUsers(query, signal)
  const fetchSearchableUser: FetchSearchableUserFn = (
    publicId,
    params,
    signal,
  ) => searchableUserStore.fetchUser(publicId, params, signal)
  const getSearchableUsers: GetSearchableUsersFn = () =>
    searchableUserStore.searchableUsers.items

  const createCustomer: CreateCustomerFn = (params) =>
    customerStore.createCustomer(params)
  const bulkCreateCustomers: BulkCreateCustomersFn = (params) =>
    bulkCreateCustomersInBatches(
      params.customers,
      customerStore,
      params.workspaceId,
      params.advisorUserPublicId,
    )

  const fetchClientAssignment: FetchClientAssignmentFn = (
    workspaceId,
    clientUserPublicId,
    signal,
  ) =>
    clientAssignmentStore.fetchClientAssignment(
      workspaceId,
      clientUserPublicId,
      signal,
    )

  const primeProvider: PrimeProviderFn = (props) =>
    iamProviderStore.primeProvider(props)
  const ensureTermsAccepted: EnsureTermsAcceptedFn = (workspaceId) =>
    termsModalViewModel.ensureTermsAccepted(workspaceId)

  const { workspaceSelectorVm, workspaceSignupVm } = authenticationViewModel
  const hasMultipleWorkspaces: HasMultipleWorkspacesFn = () =>
    workspaceSelectorVm.memberships.length > 1
  const isWorkspaceSelectorOpen: IsWorkspaceSelectorOpenFn = () =>
    workspaceSelectorVm.selector.showing
  const toggleWorkspaceSelector: ToggleWorkspaceSelectorFn = () =>
    workspaceSelectorVm.toggleSelector()
  const openWorkspaceSearch: OpenWorkspaceSearchFn = () =>
    workspaceSignupVm.openWorkspaceSearch()

  const isEnrolledInMultiFactorAuth: IsEnrolledInMultiFactorAuthFn = () =>
    multiFactorAuthStore.isUserEnrolled
  const getMultiFactorAuthEnrollmentUrl: GetMultiFactorAuthEnrollmentUrlFn =
    () => multiFactorAuthStore.fetchEnrollmentUrl()
  const getEnterpriseConnection: GetEnterpriseConnectionFn = () =>
    sessionStore.enterpriseConnection

  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (m) =>
      m.InternalIamFeature({
        ...options,
        memberStore,
        skillStore,
        roleStore,
        teamStore,
        teamMemberStore,
        teamRoleStore,
        invitationStore,
        iamProviderStore,
        userDataStore,
      }),
  )

  const createProDetailsViewModel = internal.lazy<ProDetailsViewModelFactory>(
    (m) => m.createProDetailsViewModel,
  )

  const proSoftwareReviewModalViewModel = new ProSoftwareReviewModalViewModel(
    createProDetailsViewModel,
    userDataStore,
    localStorage,
  )

  lastSeenSync(
    sessionStore.currentMember$,
    authStore.logout$,
    memberStore.updateLastLogin.bind(memberStore),
    memberStore.updateLastSeen.bind(memberStore),
  ).subscribe()

  sessionStore.currentMember$.subscribe((member) => {
    void termsModalViewModel.checkTermsForMember(member)
    void proSoftwareReviewModalViewModel.maybeShow(member)
  })

  workspacePathRestore(
    sessionStore.currentMember$,
    options.navigate,
    options.getLocation,
  ).subscribe()

  // Subscribed for the app's lifetime, since logging in elsewhere can happen
  // while no component that could listen is mounted.
  authStore.reload$.subscribe(() => window.location.reload())

  return {
    // Internal
    internal,
    authStore,
    userStore,
    userDataStore,
    acquireCompleteRegistrationViewModel,
    termsModalViewModel,
    proSoftwareReviewModalViewModel,
    authenticationViewModel,
    activate,

    // Public
    readServerRenderedWorkspace,
    token$,
    getCurrentWorkspace,
    findCurrentWorkspace,
    currentWorkspace$: sessionStore.currentWorkspace$,
    getCurrentUser,
    getCurrentMember,
    primeUser,
    waitForAuthResolution,
    currentUser$: sessionStore.currentUser$,
    currentMember$: sessionStore.currentMember$,
    logout$: authStore.logout$,
    logout,
    primeWorkspace,
    getWorkspace,
    fetchWorkspace,
    listWorkspaces,
    getWorkspaceSelection,
    workspaceSelection$,
    getCurrentUserWorkspaceMemberships,
    listWorkspaceMemberships,
    listUserWorkspaceMemberships,
    primeMember,
    primeMemberByPublicId,
    getWorkspaceMembers,
    fetchMember,
    fetchMembersByPublicIds,
    searchMembers,
    searchMembersByPermission,
    createMember,
    createMemberManually,
    updateMember,
    addMemberRole,
    addMemberSkills,
    removeMemberSkills,
    impersonateMember,
    generateMemberMagicLink,
    joinWorkspaceAsAdmin,
    promoteToOwner,
    primeTeam,
    getTeam,
    getWorkspaceTeams,
    listTeams,
    listUserTeams,
    getTeamMember,
    getTeamMembers,
    filterTeamMembers,
    getTeamMembersForWorkspaceMember,
    fetchTeamMember,
    listUserTeamMembers,
    listUserTeamMembersForWorkspace,
    listTeamMembers,
    listTeamMembersForWorkspaceMember,
    addTeamMember,
    listRoles,
    getWorkspaceRoles,
    listSkills,
    getWorkspaceSkills,
    listTeamRoles,
    getWorkspaceTeamRoles,
    getUserData,
    currentUserData$: userDataStore.currentUserData$,
    fetchUserData,
    updateUserData,
    searchUsers,
    fetchSearchableUser,
    getSearchableUsers,
    createCustomer,
    bulkCreateCustomers,
    fetchClientAssignment,
    primeProvider,
    createProDetailsViewModel,
    ensureTermsAccepted,
    hasMultipleWorkspaces,
    isWorkspaceSelectorOpen,
    toggleWorkspaceSelector,
    openWorkspaceSearch,
    isEnrolledInMultiFactorAuth,
    getMultiFactorAuthEnrollmentUrl,
    getEnterpriseConnection,
  }
}
